import { faCheck, faXmark } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useSelector } from 'react-redux'
import { setTableData } from 'store/Course/actions'
import { useLocation } from 'react-router'
import { changeGridAnswer } from 'store/Quiz/actions'
import { addGridQuestionsScores } from 'store/Quiz/actions'

const GridQuestions = ({
  editMode = true,
  takeQuiz = false,
  viewMode = false,
  tableData: viewData = null,
  gradingMode = false,
  questionPoints = 0,
  setGridScores,
  questionId,
  gradedScores,
}) => {
  const dispatch = useDispatch()
  const location = useLocation()
  const { tableData, quiz } = useSelector((store) => store.courses)
  const [initiallyFilled, setInitiallyFilled] = useState([])
  const userQuiz = useSelector((state) => state.quizes)

  const addRow = () => {
    dispatch(setTableData([...tableData, Array(tableData[0].length).fill('')]))
  }

  const addColumn = () => {
    dispatch(setTableData(tableData.map((row) => [...row, ''])))
  }

  const removeRow = (index) => {
    // eslint-disable-next-line
    if (!confirm('Are you sure you want to delete this row?')) {
      return
    }

    if (tableData.length > 2) {
      const updatedData = [...tableData]
      updatedData.splice(index, 1)
      dispatch(setTableData(updatedData))
    }
  }

  const removeColumn = (colIndex) => {
    // eslint-disable-next-line
    if (!confirm('Are you sure you want to delete this column?')) {
      return
    }

    if (tableData[0].length > 2) {
      const updatedData = tableData.map((row) =>
        row.filter((_, index) => index !== colIndex)
      )
      dispatch(setTableData(updatedData))
    } else {
      alert('Grid should have at least two columns!')
    }
  }

  const handleChange = (rowIndex, colIndex, value) => {
    const updatedData = [...tableData]
    updatedData[rowIndex][colIndex] = value
    dispatch(setTableData(updatedData))
    if (takeQuiz) dispatch(changeGridAnswer(updatedData))
  }

  useEffect(() => {
    if (editMode) {
      if (quiz?.questions[quiz?.activeIndex].tableData) {
        dispatch(setTableData(quiz?.questions[quiz?.activeIndex]?.tableData))
      }
    } else if (takeQuiz) {
      const index = location?.search?.split('=')?.at(1)
      const tableData = quiz?.questions[index]?.tableData
      dispatch(setTableData(tableData))
      setInitiallyFilled(
        tableData?.map((row) => row?.map((cell) => cell !== ''))
      )
    }

    return () => {
      dispatch(
        setTableData([
          ['', ''],
          ['', ''],
          ['', ''],
        ])
      )
    }
  }, [
    quiz?.activeIndex,
    quiz?.questions,
    dispatch,
    location?.search,
    takeQuiz,
    editMode,
    viewMode,
    userQuiz?.submissions,
    viewData,
  ])

  return (
    <>
      {viewMode ? (
        <ViewTable
          tableData={viewData}
          gradingMode={gradingMode}
          questionPoints={questionPoints}
          setGridScores={setGridScores}
          questionId={questionId}
          gradedScores={gradedScores}
        />
      ) : (
        <div className='p-4'>
          <table className='border-collapse border border-gray-300 w-100'>
            {tableData.map((row, rowIndex) =>
              rowIndex === 0 ? (
                <thead>
                  <tr>
                    {tableData[0].map((_, colIndex) => (
                      <Header
                        key={colIndex}
                        colIndex={colIndex}
                        removeColumn={removeColumn}
                        handleChange={handleChange}
                        row={row}
                        editMode={editMode}
                      />
                    ))}
                    <th className='bg-gray-100 border border-gray-300'></th>
                  </tr>
                </thead>
              ) : (
                <tbody>
                  <tr key={rowIndex}>
                    {row.map((cell, colIndex) => (
                      <td key={colIndex} className='border border-gray-300 p-1'>
                        <input
                          type='text'
                          value={cell}
                          onChange={(e) =>
                            handleChange(rowIndex, colIndex, e.target.value)
                          }
                          className='form-control w-full'
                          disabled={
                            !editMode &&
                            initiallyFilled?.at(rowIndex)?.at(colIndex)
                          }
                        />
                      </td>
                    ))}
                    {editMode && (
                      <td className='text-center border border-gray-300'>
                        <FontAwesomeIcon
                          style={{ cursor: 'pointer' }}
                          onClick={() => removeRow(rowIndex)}
                          icon={faXmark}
                          size='2x'
                          className={`${tableData.length <= 2 && 'invisible'}`}
                        />
                      </td>
                    )}
                  </tr>
                </tbody>
              )
            )}
          </table>

          {editMode && (
            <div className='mt-3'>
              <button className='btn btn-outline-info mr-2' onClick={addRow}>
                <i className='fas fa-plus mr-1'></i> Add row
              </button>
              <button className='btn btn-outline-info' onClick={addColumn}>
                <i className='fas fa-plus mr-1'></i> Add column
              </button>
            </div>
          )}
        </div>
      )}
    </>
  )
}

const ViewTable = ({
  tableData,
  gradingMode,
  questionPoints,
  setGridScores,
  questionId,
  gradedScores,
}) => {
  const dispatch = useDispatch()
  const [scores, setScores] = useState([])

  const handleScoreUpdate = (rowIndex, value) => {
    setScores((prevScores) => {
      const updatedScores = prevScores.map((score, index) =>
        index === rowIndex ? (score === value ? 0 : value) : score
      )

      dispatch(addGridQuestionsScores(updatedScores, questionId))

      return updatedScores
    })
  }

  useEffect(() => {
    const array = Array.from({ length: tableData?.length }, (_, index) => 0)
    setScores(array)
  }, [tableData?.length])

  useEffect(() => {
    if (!gradingMode) return
    const results = scores?.slice(1) || []
    const correct = results?.filter((el) => el === 1)?.length || 0
    const percentage = results.length > 0 ? (correct / results.length) * 100 : 0
    const finalScore = (percentage / 100) * questionPoints

    setGridScores((prevGridScores) => {
      return prevGridScores.map((item) =>
        item[questionId] !== undefined ? { [questionId]: finalScore } : item
      )
    })
  }, [scores, questionPoints, gradingMode])

  useEffect(() => {
    if (!gradingMode && gradedScores && gradedScores.length) {
      setScores(gradedScores)
    }
  }, [gradedScores, gradingMode])

  return (
    <div className='p-4'>
      <table className='border-collapse border border-gray-300 w-100'>
        {tableData.map((row, rowIndex) =>
          rowIndex === 0 ? (
            <thead>
              <tr>
                {tableData[0].map((_, colIndex) => (
                  <Header key={colIndex} colIndex={colIndex} row={row} />
                ))}
                <th className='bg-gray-100 border border-gray-300'></th>
              </tr>
            </thead>
          ) : (
            <tbody>
              <tr key={rowIndex}>
                {row.map((cell, colIndex) => (
                  <td
                    key={colIndex}
                    className={`border border-gray-300 p-1 ${
                      gradingMode || gradedScores
                        ? scores[rowIndex] === 1
                          ? 'bg-success'
                          : scores[rowIndex] === -1
                          ? 'bg-danger'
                          : ''
                        : ''
                    } `}
                  >
                    <input
                      type='text'
                      value={cell}
                      className='form-control w-full'
                      disabled
                    />
                  </td>
                ))}
                {gradingMode && (
                  <td className='text-center border border-gray-300'>
                    <FontAwesomeIcon
                      style={{ cursor: 'pointer' }}
                      icon={faCheck}
                      size='lg'
                      className={`text-success mr-3`}
                      onClick={() => handleScoreUpdate(rowIndex, 1)}
                    />
                    <FontAwesomeIcon
                      style={{ cursor: 'pointer' }}
                      icon={faXmark}
                      size='lg'
                      className={`text-danger`}
                      onClick={() => handleScoreUpdate(rowIndex, -1)}
                    />
                  </td>
                )}
              </tr>
            </tbody>
          )
        )}
      </table>
    </div>
  )
}

const Header = ({ colIndex, removeColumn, handleChange, row, editMode }) => {
  const [showX, setShowX] = useState(false)

  return (
    <th
      onMouseEnter={() => setShowX(true)}
      onMouseLeave={() => setShowX(false)}
      key={colIndex}
      className='bg-light text-center position-relative py-3'
    >
      <div className='d-flex justify-content-center align-items-center w-100 position-relative'>
        <input
          type='text'
          value={row[colIndex]}
          onChange={(e) => handleChange(0, colIndex, e.target.value)}
          className='border-0 bg-transparent text-left w-100 ml-2'
          style={{ outline: 'none' }}
          disabled={!editMode}
        />
        {editMode && (
          <FontAwesomeIcon
            style={{ cursor: 'pointer', position: 'absolute', right: '20px' }}
            onClick={() => removeColumn(colIndex)}
            className={`${!showX && 'invisible'}`}
            icon={faXmark}
            size='2x'
          />
        )}
      </div>
    </th>
  )
}

export default GridQuestions
